import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { formatIfDefined, isDefined } from '@adornis/bh-util/util.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { differenceInDays, format } from 'date-fns';
import { getProductByID } from '../api/product.js';
import { getUserByID } from '../api/users.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { getParticipationByID } from '../api/participation-helper.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { ParticipationEinzelberatung } from '../entities/ParticipationEinzelberatung.js';
import { Setting, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { Workshop } from '../entities/workshop.js';
import type { ParticipationState } from '../enums/enums.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { buildNewsletterOptinLink, buildRegistrationsHTML } from '../mails/mail-helpers.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappWithEmailFallback } from './_helper.js';

// Mail template for Veranstaltungseintragung durch Admin

defineMailTemplate(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Veranstaltungseintragung durch den Admin',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>gute Neuigkeiten, soeben ist ein Platz in unserer Veranstaltung frei geworden. Da Sie auf dem n&auml;chsten Platz der Warteliste standen, haben wir Sie verbindlich in die Teilnehmer*innenliste eingetragen. Nachstehend f&uuml;hren wir nochmals die wesentlichen Informationen der Veranstaltung auf:</p>
<p>{{changed-registrations}}</p>
<p>Eine Auflistung all Ihrer Workshops und die M&ouml;glichkeit, sich wieder abmelden zu k&ouml;nnen, finden Sie in Ihrer Anmeldeverwaltung: {{login-link}}</p>
<p><strong>Sie k&ouml;nnen nicht teilnehmen?&nbsp;</strong></p>
<p>Falls die den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung. Eine Abmeldung ist nur bis sp&auml;testens 6 Tage vor Workshop-Beginn m&ouml;glich. Nach Ablauf dieser Frist ist eine Abmeldung nur <a href="mailto:contact@anmeldung.uni-frankfurt.de">per Mail</a> mit Angabe eines triftigen Grundes m&ouml;glich. Bei Nichterscheinen ohne vorherige Absage behalten wir uns vor, Sie von weiteren Workshops auszuschlie&szlig;en bzw. Ihnen entstehende Kosten in Rechnung zu stellen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Good news, a place has just become available in our event. As you were on the next place on the waiting list, we have bindingly entered you in the list of participants.</p>
<p>Below we list again the essential information of the event:</p>
<p>{{changed-registrations}}</p>
<p>You will find a list of all your workshops and the possibility to cancel your registration in your registration administration: {{login-link}}</p>
<p><strong>You are unable to attend? Please deregister and thereby enable other students to participate.</strong> If you are unable to attend the event, please deregister promptly in your registration administration. Students on the waiting list will then benefit from your deregistration. Deregistration is only possible up to 6 days before the start of the workshop. After this deadline, cancellation is only possible by e-mail with a valid reason. In the case of non-attendance without prior cancellation, we reserve the right to exclude you from further workshops or to charge you for any costs incurred.</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN),
  attachments: [],
  displayName: 'Veranstaltungseintragung durch Admin',
  editorHint: () => institutionMailHint(MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN),
});

export async function notifyVeranstaltungseintragungDurchAdmin(participationID: string) {
  const participation = await getParticipationByID(participationID)({
    _id: 1,
    _class: 1,
    userID: 1,
    state: 1,
    createdAt: 1,
    updatedAt: 1,
    productID: 1,
    waitingListPlace: 1,
    timeslotID: 1,
  } as any);
  if (!participation) throw new Error('no participation found');
  (participation as any).product = await getProductByID(participation.productID as string)({
    _class: 1,
    _id: 1,
    name: 1,
    description: 1,
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    settings: {
      ...selectionSet(() => Setting, 2),
      blockAutomaticMoveUpDaysInAdvance: 1,
      inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
      blockSignOffDaysInAdvance: 1,
      inheritedBlockSignOffDaysInAdvance: 1,
    },
    ...({
      capacity: 1,
      hideCapacity: 1,
      appointments: { start: 1, end: 1, location: 1, name: 1 },
      slots: { _id: 1, start: 1, end: 1 },
    } as any),
  });
  const user = await getUserByID(participation.userID as string)({
    _id: 1,
    email: 1,
    firstName: 1,
    lastName: 1,
    title: 1,
    username: 1,
    whatsappNotification: 1,
    whatsappPhone: 1,
    suspentions: { _id: 1, institutionID: 1 },
    receivesNewsletter: 1,
    roles: { _id: 1, name: 1, contexts: 1, permissions: 1 },
  });
  if (!user) throw new Error('no user found');

  // Get institution with color definitions
  const institution = await getInstitutionByID(participation.product?.institutionID as string)({
    ...Institution.allFields,
    _id: 1,
    name: 1,
    colorDefinitions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('no institution found');

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await getMailTemplateForInstitution(
    participation.product?.institutionID,
    MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN,
  )(InstitutionMailTemplate.allFields);

  if (!mailTemplate) throw new Error('no mail template found');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const start = (participation.product as Workshop)?.appointments?.[0]?.start;
  const daysTillEvent = start ? differenceInDays(start, new Date()).toFixed(0) : undefined;
  let dates: string | undefined = (participation.product as Workshop)?.appointments
    ?.map(a => formatIfDefined(a.start, 'dd.MM.yyyy'))
    .filter(isDefined)
    .join(', ');
  if (!dates) dates = start ? format(start, 'dd.MM.yyyy') : undefined;
  if (participation instanceof ParticipationEinzelberatung) {
    const slot = participation.product?.slots.find(slot => slot._id === participation.timeslotID);
    dates = slot?.start ? format(slot.start, 'dd.MM.yyyy') : undefined;
  }

  const sendEmail = async () => {
    const mailVars = {
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name ?? '',
      [MailTemplateVars.PRODUCT_NAME]: participation.product?.name ?? '',
      [MailTemplateVars.EVENT_DATES]: dates,
      [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
      [MailTemplateVars.TITLE]: user.title ?? '',
      [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
      [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: buildRegistrationsHTML([
        {
          name: participation.product?.name ?? '',
          state: participation.state,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation.waitingListPlace,
          appointments: (participation.product as Workshop)?.appointments || [],
          englishVersion: false,
        },
      ]),
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: buildRegistrationsHTML([
        {
          name: participation.product?.name ?? '',
          state: participation.state as ParticipationState,
          topic: participation.product?.topic,
          language: participation.product?.language,
          waitingListPlace: participation.waitingListPlace,
          appointments: (participation.product as Workshop)?.appointments || [],
          englishVersion: true,
        },
      ]),
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK]: buildNewsletterOptinLink(token, 'de'),
      [MailTemplateVars.NEWSLETTER_OPTIN_LINK_EN]: buildNewsletterOptinLink(token, 'en'),
    };

    const { subject, content } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: mailVars as any,
    })) as { subject: string; content: any };

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    return await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID: participation.product?.institutionID,
    });
  };

  const notificationType = await getNotificationType(user, mailTemplate, institution._id);
  if (notificationType === 'whatsapp') {
    await sendWhatsappWithEmailFallback({
      institutionID: institution._id,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone!, email: user.email },
      mailTemplate,
      mailTemplateType: MailTemplateType.VERANSTALTUNGSEINTRAGUNG_DURCH_ADMIN,
      parameters: [
        InstitutionMailTemplate.mapFirstNameVar(user) || '-',
        InstitutionMailTemplate.mapLastNameVar(user) || '-',
        participation.product?.name || '-',
        participation.product?._class === Workshop._class
          ? InstitutionMailTemplate.workshopTerminString(participation.product as any)
          : InstitutionMailTemplate.einzelberatungTerminString(
              participation.product as any,
              (participation as any as ParticipationEinzelberatung).timeslotID,
            ),
      ],
      sendEmail,
    });
  } else if (notificationType === 'email') {
    return await sendEmail();
  }
}
